import { Badge, Image, Popover, Table, Typography } from "antd"
import { OrderLocation } from "../../../components/Orders/OrderLocation"
import moment from "moment"
import { returnProperMachineTag } from "../../../components/utils/ReturnProperMachineTag"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import { useAuthContext } from "../../../hooks/useAuthContext"
import UserActionDrawer from "../../../components/Manager/Order/Action/UserActionDrawer"
import { useEffect, useState } from "react"
import { collection, onSnapshot, query, where } from "firebase/firestore"
import { db } from "../../../firebase/config"
import { filterOrdersByRole } from "../../../utils/filterOrdersByRole"

const { Text, Link } = Typography

export default function TapeOrders({ activeKey, user }) {
  const { admin, semiAdmin, devSpecialist } = useAuthContext()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)

  //gets data
  useEffect(() => {
    if (activeKey === 2) {
      setLoading(true)
      const dataR = collection(db, "orders")
      let tapeQ = (admin || semiAdmin || devSpecialist)
        ? query(dataR, where("orderStatus", "==", 3.5))
        : query(dataR, where("orderStatus", "==", 3.5), where("uid", "==", user.uid))
      const unsubscribe = onSnapshot(tapeQ, (snapshot) => {
        const tapeOrd = snapshot.docs.map((doc) => ({
          id: doc.id, // include the document ID here
          ...doc.data(),
        }))
        setTimeout(() => {
          const filtered = filterOrdersByRole(tapeOrd, { admin, semiAdmin, devSpecialist })
          setData(filtered)
          setLoading(false)
        }, 200)
        setLoading(false)
      })

      return () => {
        unsubscribe()
      }
    }
  }, [activeKey, admin, semiAdmin, devSpecialist, user])

  //loc filterr
  let locationFilter =
    data &&
    data.reduce((accumulator, item) => {
      const filter = item.orderStatus
      // Only add new item if it does not exist yet
      if (!accumulator.some((filterItem) => parseInt(filterItem.value, 10) === filter)) {
        accumulator.push({
          text: OrderLocation(filter),
          value: filter,
        })
      }

      return accumulator
    }, [])

  //client filter
  let clientFIlters =
    data &&
    data.reduce((accumulator, item) => {
      const clientName = item.client.client
      const existingItem = accumulator.find((filterItem) => filterItem.text === clientName)

      if (existingItem) {
        existingItem.value = clientName
      } else {
        accumulator.push({
          text: clientName,
          value: clientName,
        })
      }

      return accumulator
    }, [])

  let productFilters =
    data &&
    data.reduce((accumulator, item) => {
      const productName = item.selectedProduct.product.productName
      const existingItem = accumulator.find((filterItem) => filterItem.text === productName)

      if (existingItem) {
        existingItem.value = productName
      } else {
        accumulator.push({
          text: productName,
          value: productName,
        })
      }

      return accumulator
    }, [])

  //default columns for table
  const defaultColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "center",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      render: (value, record) => OrderAndPrefix(record),
    },
    {
      title: "Location",
      dataIndex: "orderStatus",
      align: "center",
      render: (value) => <Badge status="processing" text={OrderLocation(value)} />,
      filters: locationFilter,
      onFilter: (value, record) => record.orderStatus === value,
      filterSearch: true,
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      filters: clientFIlters,
      onFilter: (value, record) => record.client.client.startsWith(value),
      filterSearch: true,
    },
    {
      title: "Machine",
      dataIndex: ["selectedProduct", "machine"],
      align: "center",
      render: (value) => {
        return returnProperMachineTag(value)
      },
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      align: "center",
      render: (value, record) => {
        // Safely access changableSpace using optional chaining and logical OR
        const changableSpace = record.selectedProduct?.advanced?.changableSpace || false
        if (changableSpace) {
          return (
            <Text>
              {value} - ({record.changableSpaceData})
            </Text>
          )
        } else {
          return <Text>{value}</Text>
        }
      },
    },
    {
      title: "Manufacturer",
      dataIndex: ["manufacturer", "company"],
      align: "center",
    },
    {
      title: "Roll Count",
      align: "center",
      render: (record) => {
        if (record.selectedProduct.machine === "Tape") {
          return <Text>{record.rollCount}</Text>
        } else {
          return <Text>{record.labelCount}</Text>
        }
      },
    },
    {
      title: "Comment",
      dataIndex: "salesComment",
      align: "center",
      render: (value, record) => {
        if (value != null)
          return (
            <>
              <Popover
                content={
                  <div>
                    <p>
                      {record.user} -{value}
                    </p>
                  </div>
                }
                title="Title">
                <Link>comment</Link>
              </Popover>
            </>
          )
      },
    },
    {
      title: "Technical",
      dataIndex: ["selectedProduct", "technicalPage"],
      align: "center",
      render: (value) => {
        return <Image src={value} width={25} height={25} />
      },
    },
    {
      title: "Due date",
      dataIndex: "orderDueDate",
      align: "center",
      sorter: (a, b) => a.orderDueDate.seconds - b.orderDueDate.seconds,
      render: (value) => <Text>{moment.unix(value.seconds).format("DD/MM/YYYY")}</Text>,
    },
    {
      title: "Created",
      dataIndex: "created",
      align: "center",
      sorter: (a, b) => a.created.seconds - b.created.seconds,
      render: (value) => <Text>{moment.unix(value.seconds).format("DD/MM/YYYY")}</Text>,
    },
    {
      title: "Action",
      align: "center",
      render: (value) => <UserActionDrawer data={value} />,
    },
  ]

  return (
    <>
      <Table
        style={{ marginTop: "15px" }}
        size="small"
        loading={loading}
        columns={defaultColumns}
        bordered
        dataSource={data}
        rowKey={(record) => record.id}
        pagination={{ defaultPageSize: "12", showSizeChanger: true }}
      />
    </>
  )
}
